import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, ShieldCheck, ShieldOff, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";

const input = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";
const SCHOOL_STATUS = { pending_payment: "En attente de paiement", paid: "Payée", delivered: "Remise", cancelled: "Annulée" };
const FIELDS = [["name", "Nom"], ["email", "E-mail"], ["phone", "Téléphone"], ["address_line", "Adresse"], ["postal_code", "Code postal"], ["city", "Ville"]];
const date = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");

export default function CustomerDetail({ customerId, onBack }) {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});
  const [schools, setSchools] = useState([]);
  const [pts, setPts] = useState({ delta: "", reason: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/customers/${customerId}`).then(({ data }) => {
    setC(data);
    setForm({ ...Object.fromEntries(FIELDS.map(([k]) => [k, data[k] || ""])), school_id: data.school_id || "" });
  }).catch(() => { toast.error("Client introuvable"); onBack(); });

  useEffect(() => {
    load();
    api.get("/admin/ecoles").then(({ data }) => setSchools(data.schools)).catch(() => {});
    // eslint-disable-next-line
  }, [customerId]);

  const run = async (fn, ok) => {
    try { const { data } = await fn(); toast.success(data.message || ok); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    await run(() => api.put(`/admin/customers/${customerId}`, { ...form, school_id: form.school_id || null }));
    setSaving(false);
  };

  const adjust = async () => {
    const delta = parseInt(pts.delta, 10);
    if (!delta) return toast.error("Indiquez un nombre de points (ex. 50 ou -20)");
    if (pts.reason.trim().length < 2) return toast.error("Indiquez le motif");
    await run(() => api.post(`/admin/customers/${customerId}/points`, { delta, reason: pts.reason.trim() }));
    setPts({ delta: "", reason: "" });
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer définitivement le compte de ${c.email} ? Ses commandes sont conservées.`)) return;
    try { await api.delete(`/admin/customers/${customerId}`); toast.success("Compte supprimé"); onBack(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!c) return <p className="flex items-center justify-center gap-2 py-16 text-bark-soft"><Loader2 className="animate-spin text-amber-deep" size={20} /> Chargement…</p>;

  return (
    <div data-testid="customer-detail" className="space-y-6">
      <button data-testid="customer-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-bark-soft hover:text-amber-deep"><ArrowLeft size={16} /> Tous les clients</button>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">{c.name || c.email}</h1>
          <p className="text-sm text-bark-soft">Client depuis le {date(c.created_at)}{c.school_name ? ` · Parent — ${c.school_name}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="customer-toggle-status" onClick={() => run(() => api.put(`/admin/customers/${customerId}/status`, { disabled: !c.disabled }))}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${c.disabled ? "border-sage/40 text-sage hover:bg-sage-light" : "border-amber-800/25 text-bark hover:border-terra hover:text-terra"}`}>
            {c.disabled ? <><ShieldCheck size={15} /> Réactiver</> : <><ShieldOff size={15} /> Désactiver</>}
          </button>
          <button data-testid="customer-delete" onClick={remove} className="inline-flex items-center gap-2 rounded-full border border-terra/30 px-4 py-2 text-sm font-medium text-terra hover:bg-terra/10"><Trash2 size={15} /> Supprimer</button>
        </div>
      </div>
      {c.disabled && <p data-testid="customer-disabled-banner" className="rounded-xl bg-terra/10 px-4 py-3 text-sm font-medium text-terra">Compte désactivé : ce client ne peut plus se connecter.</p>}

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <form onSubmit={save} data-testid="customer-edit-form" className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <p className="font-serif text-lg font-semibold text-bark">Coordonnées</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <label key={k} className={k === "address_line" ? "sm:col-span-2" : ""}>
                <span className="mb-1 block text-xs font-medium text-bark-soft">{label}</span>
                <input data-testid={`customer-field-${k}`} value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={input} />
              </label>
            ))}
            <label className="sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-bark-soft">École rattachée (parent d'élève)</span>
              <select data-testid="customer-field-school" value={form.school_id} onChange={(e) => setForm((f) => ({ ...f, school_id: e.target.value }))} className={input}>
                <option value="">Aucune école</option>
                {schools.map((s) => <option key={s.school_id} value={s.school_id}>{s.name}{s.city ? ` — ${s.city}` : ""}</option>)}
              </select>
            </label>
          </div>
          <button data-testid="customer-save" disabled={saving} className="rounded-full bg-amber-deep px-6 py-2.5 text-sm font-semibold text-white hover:bg-honey disabled:opacity-60">{saving ? "Enregistrement…" : "Enregistrer"}</button>
        </form>

        <div className="space-y-4 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
          <p className="font-serif text-lg font-semibold text-bark">Fidélité</p>
          <p data-testid="customer-points" className="flex items-center gap-2 font-serif text-4xl font-semibold text-amber-deep"><Star size={26} className="fill-honey text-honey" /> {c.loyalty_points || 0} <span className="font-sans text-sm font-normal text-bark-soft">points</span></p>
          <div className="grid grid-cols-[90px_1fr] gap-2">
            <input data-testid="points-delta" type="number" value={pts.delta} onChange={(e) => setPts((p) => ({ ...p, delta: e.target.value }))} placeholder="±50" className={input} />
            <input data-testid="points-reason" value={pts.reason} onChange={(e) => setPts((p) => ({ ...p, reason: e.target.value }))} placeholder="Motif (geste commercial…)" className={input} />
          </div>
          <button data-testid="points-submit" onClick={adjust} className="w-full rounded-full border-2 border-amber-700/30 py-2.5 text-sm font-medium text-amber-900 hover:border-amber-deep hover:bg-amber-50">Ajuster les points</button>
          {c.adjustments.length > 0 && (
            <ul className="divide-y divide-amber-900/10 text-sm">
              {c.adjustments.map((a) => (
                <li key={a.id} className="flex justify-between gap-2 py-2"><span className="text-bark-soft">{date(a.created_at)} · {a.reason}</span><span className={`font-semibold ${a.delta > 0 ? "text-sage" : "text-terra"}`}>{a.delta > 0 ? "+" : ""}{a.delta}</span></li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <OrderHistory shop={c.shop_order_list} school={c.school_order_list} />
    </div>
  );
}

function OrderHistory({ shop, school }) {
  const rows = [
    ...shop.map((o) => ({ id: o.session_id, kind: "Boutique", at: o.created_at, total: Math.round((o.amount || 0) * 100), status: o.reassigned_to_school ? `Réassignée — ${o.reassigned_to_school.school_name}` : o.payment_status === "paid" ? "Payée" : o.payment_status, items: (o.items || []).map((i) => `${i.quantity} × ${i.name}`) })),
    ...school.map((o) => ({ id: o.order_id, kind: "École", at: o.created_at, total: o.subtotal_cents, status: SCHOOL_STATUS[o.status] || o.status, items: (o.items || []).map((i) => `${i.qty} × ${i.name}`), student: `${o.student_first} ${o.student_last} (${o.student_class})` })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  return (
    <div data-testid="customer-history" className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
      <p className="mb-3 font-serif text-lg font-semibold text-bark">Historique des commandes</p>
      {rows.length === 0 ? <p className="py-6 text-center text-sm text-bark-soft">Aucune commande.</p> : (
        <ul className="divide-y divide-amber-900/10">
          {rows.map((r) => (
            <li key={r.id} data-testid={`customer-order-${r.id}`} className="flex flex-wrap items-start justify-between gap-3 py-3 text-sm">
              <div>
                <p className="font-medium text-bark"><span className={`mr-2 rounded-full px-2 py-0.5 text-xs ${r.kind === "École" ? "bg-amber-100 text-amber-800" : "bg-sand text-bark-soft"}`}>{r.kind}</span>{date(r.at)} · n° {r.id.slice(-8).toUpperCase()}</p>
                {r.student && <p className="mt-0.5 text-xs text-bark-soft">Élève : {r.student}</p>}
                <p className="mt-0.5 text-xs text-stone-mute">{r.items.join(" · ")}</p>
              </div>
              <div className="text-right"><p className="font-semibold text-bark">{formatPrice(r.total)}</p><p className="text-xs text-bark-soft">{r.status}</p></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
